import { QueryClient } from "@tanstack/react-query";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export function buildApiUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${API_BASE_URL}${path}`;
}

function getCsrfToken(): string | null {
  const match = document.cookie.match(/(^|;\s*)_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : null;
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function csrfHeaders(method?: string): Record<string, string> {
  if (method && MUTATING_METHODS.has(method.toUpperCase())) {
    const token = getCsrfToken();
    if (token) {
      return { "x-csrf-token": token };
    }
  }
  return {};
}

export function secureFetch(url: string, init?: RequestInit): Promise<Response> {
  const method = init?.method?.toUpperCase() || "GET";
  return fetch(url, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.headers ?? {}),
      ...csrfHeaders(method),
    },
  });
}

async function defaultQueryFn({ queryKey }: { queryKey: readonly unknown[] }): Promise<any> {
  const path = queryKey.filter(segment => segment !== undefined && segment !== null).join('/');
  const url = buildApiUrl(path);
  
  const response = await fetch(url, {
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error(`401: Unauthorized - ${response.statusText}`);
    }
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response.json();
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      staleTime: 1000 * 60,
      retry: false,
    },
  },
});

export async function apiRequest(
  methodOrPath: string,
  pathOrOptions?: string | RequestInit,
  body?: unknown
): Promise<any> {
  let url: string;
  let options: RequestInit;

  const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];
  if (HTTP_METHODS.includes(methodOrPath.toUpperCase()) && typeof pathOrOptions === "string") {
    url = buildApiUrl(pathOrOptions);
    options = {
      method: methodOrPath.toUpperCase(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };
  } else {
    url = buildApiUrl(methodOrPath);
    options = (pathOrOptions as RequestInit) ?? {};
  }

  const method = (options.method || "GET").toUpperCase();

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
      ...csrfHeaders(method),
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error(`401: Unauthorized - ${response.statusText}`);
    }
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `${response.status}: ${response.statusText}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return null;
}
